import { useEffect, useMemo, useState } from 'react';
import { GEWERK_TEXT, api, darf, datum, heuteIso, menge, zeitpunkt,
         type Anmelder }
  from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { Karte, Leer } from './Geruest.tsx';
import {
  heuteTag, istWochenende, montagVon, plusTage, tageAb, type Tag,
} from './rastertage.ts';
import { useFrisch } from './Frisch.tsx';

/**
 * Der Betrieb: fünf Dinge, die es seit dem ersten Tag in der Datenbank gibt
 * und für die es bis zum 24.08.2026 keinen einzigen Bildschirm gab.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * Einblasstop, Verfügbarkeit, Pollercodes, Rechnungsprüfung und der Versand
 * von Hand. Keiner davon bekommt einen eigenen Menüpunkt: sie gehören dorthin,
 * wo die Frage entsteht. Der Einblasstop an den Auftrag, die Verfügbarkeit in
 * den Terminplaner, die Pollercodes in die Stammdaten, die Rechnungen in die
 * Abrechnung. Ein sechster Menüpunkt für eine Tabelle mit vier Zeilen wird
 * nach zwei Wochen nicht mehr angeklickt.
 */


/**
 * DIE VIER TERMINMAILS, wie sie im Auswahlfeld stehen.
 *
 * Sie sind hier nur die BESCHRIFTUNG; welche Vorlage gilt, welcher Rückfall
 * greift und was eingesetzt wird, sagt `regeln/terminmail.ts` auf dem Server.
 * Eine zweite Fassung davon im Browser liefe beim ersten Nachtrag auseinander.
 */
const TERMINMAILS = [
  { wert: 'neu', text: 'Terminbestätigung',
    zweck: 'Der Termin ist vergeben. Nennt beide Termine des Auftrags.' },
  { wert: 'umbuchung', text: 'Terminverschiebung',
    zweck: 'Nennt den bisherigen Termin daneben.' },
  { wert: 'absage', text: 'Terminabsage',
    zweck: 'Fällt auf keinen anderen Text zurück.' },
  { wert: 'erinnerung', text: 'Terminerinnerung',
    zweck: 'Jemand muss zuhause sein und Zugang zu allen Räumen geben.' },
];
const TERMINARTEN = TERMINMAILS.map((x) => x.wert);


// ============================================================================
// 1. Einblasstop
// ============================================================================

/**
 * Den Einblasstop melden oder zurücknehmen.
 *
 * ORIGINALTEXT MARVIN (18.08.2026)
 *   „die ganzen gründe die dort als möglichkeiten aufgeführt sind wissen wir
 *    doch garnicht. wir stellen nur den einblasstop fest."
 *
 * Deshalb steht hier KEINE Gründeliste. Es gibt Meter, Richtung und eine
 * freie Bemerkung: wie weit es ging, aus welcher Richtung, und was jemand
 * beobachtet hat. Ein Auswahlfeld mit acht Ursachen erzwingt eine Antwort,
 * die niemand hat, und die steht danach als Tatsache in der Akte.
 */
export function EinblasstopDialog({
  auftragId, baustelle, zu, fertig, darfSenden = false, darfMelden = true,
}: {
  auftragId: string; baustelle?: string; zu: () => void; fertig: () => void;
  /** Darf die Meldung an die Telekom schicken (nachricht.senden). */
  darfSenden?: boolean;
  /** Darf eine Richtung eintragen (Aufmaß erfassen oder prüfen). */
  darfMelden?: boolean;
}) {
  const [d, setD] = useState<any>(null);
  const [meter, setMeter] = useState('');
  const [seite, setSeite] = useState('');
  const [bemerkung, setBemerkung] = useState('');
  const [grund, setGrund] = useState('');
  /* WELCHE MELDUNG ZURÜCKGENOMMEN WIRD: eine Richtung, oder alle.
     Marvin, 03.09.2026: „hier ist es wichtig, dass sie auch zwei Einträge
     untereinander machen können, falls von beiden Seiten geprüft wurde."
     Wer von zwei Richtungen gemeldet hat und eine davon berichtigt, darf
     nicht beide verlieren. */
  const [zuruecknehmen, setZuruecknehmen] = useState<null | { seite?: string }>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* WEITERE RICHTUNG (.272): ist schon etwas gemeldet, steht das Formular
     nicht mehr offen da. Marvin, 01.10.2026: „wenn der monteur doch schon die
     daten einträgt, warum muss ich die dann nochmal manuell eintragen?" -
     er musste nicht, aber das leere Feld darunter sah danach aus. */
  const [weitere, setWeitere] = useState(false);
  /* DIE MELDUNG AN DIE TELEKOM (.272). */
  const [tk, setTk] = useState<any>(null);
  const [tkAn, setTkAn] = useState('');
  const [tkBetreff, setTkBetreff] = useState('');
  const [tkText, setTkText] = useState('');
  const [tkErgebnis, setTkErgebnis] = useState<any>(null);

  useEffect(() => {
    if (!darfSenden) return;
    api.einblasstopTelekom(auftragId).then((v) => {
      setTk(v); setTkAn(v.an ?? ''); setTkBetreff(v.betreff ?? ''); setTkText(v.text ?? '');
    }).catch(() => setTk(null));
  }, [auftragId, darfSenden]);

  async function anTelekom() {
    setLaeuft(true); setFehler(null); setTkErgebnis(null);
    try {
      const r = await api.einblasstopTelekomSenden(auftragId, { an: tkAn, betreff: tkBetreff, text: tkText });
      setTkErgebnis(r);
      if (r.gesendet) setTk({ ...tk, gesendet_am: new Date().toISOString() });
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  useEffect(() => {
    api.einblasstop(auftragId).then((s) => {
      setD(s);
      /* DAS FORMULAR FÄNGT LEER AN, WENN SCHON ETWAS GEMELDET IST.
         Es ist dann das Feld für die ZWEITE Richtung; die erste steht
         darüber in der Liste. Vorbelegt wäre es die Einladung, die
         bestehende Meldung versehentlich zu überschreiben. */
      const offene = (s.meldungen ?? []).filter((m: any) => !m.zurueck_am);
      if (!offene.length) {
        setMeter(s.einblasstop_meter != null ? String(s.einblasstop_meter) : '');
        setSeite(s.einblasstop_seite ?? '');
        setBemerkung(s.einblasstop_bemerkung ?? '');
      }
    }).catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  async function melden() {
    setLaeuft(true); setFehler(null);
    try {
      await api.einblasstopMelden(auftragId, {
        meter: Number(String(meter).replace(',', '.')), seite, bemerkung });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function zurueck() {
    setLaeuft(true); setFehler(null);
    try {
      await api.einblasstopZurueck(auftragId, grund, zuruecknehmen?.seite);
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Einblasstop" untertitel={baustelle} breite={620}
            laeuft={laeuft} zu={zu}>
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <Fehlerkasten lage={fehler} />

          {/* WAS SCHON GEMELDET IST - EINE ZEILE JE RICHTUNG.
              ORIGINALTEXT MARVIN (03.09.2026)
                „hier ist es wichtig, dass sie auch zwei Einträge untereinander
                 machen können, falls von beiden Seiten geprüft wurde."
              Bis .90 waren Meter und Richtung je EINE Spalte am Auftrag: die
              zweite Meldung hat die erste überschrieben, still. */}
          {d.offen && !zuruecknehmen && (
            <div className="hinweis warnung">
              <b>{(d.meldungen ?? []).filter((m: any) => !m.zurueck_am).length > 1
                ? 'Für diesen Auftrag sind 2 Einblasstops gemeldet:'
                : 'Für diesen Auftrag ist ein Einblasstop gemeldet:'}</b>
              <table className="liste" style={{ marginTop: 6 }}>
                <tbody>
                  {(d.meldungen ?? []).filter((m: any) => !m.zurueck_am)
                    .map((m: any) => (
                    <tr key={m.id} style={{ cursor: 'default' }}>
                      <td>{m.seite}</td>
                      <td style={{ width: 90 }}>{menge(m.meter, 'm')}</td>
                      <td className="klein leise">
                        {zeitpunkt(m.gemeldet_am)}
                        {m.verfasser_name ? `, ${m.verfasser_name}` : ''}
                        {m.bemerkung ? `, ${m.bemerkung}` : ''}
                        {m.an_telekom_am ? `, an die Telekom ${zeitpunkt(m.an_telekom_am)}` : ''}
                      </td>
                      <td className="knopfleiste" style={{ width: 130 }}>
                        <button className="knopf klein" disabled={laeuft}
                                onClick={() => setZuruecknehmen({ seite: m.seite })}>
                          zurücknehmen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* ZURÜCKGENOMMEN HEISST NICHT GELÖSCHT. */}
          {!zuruecknehmen
            && (d.meldungen ?? []).some((m: any) => m.zurueck_am) && (
            <p className="klein leise">
              Zurückgenommen:{' '}
              {(d.meldungen ?? []).filter((m: any) => m.zurueck_am)
                .map((m: any) => `${m.seite} (${m.zurueck_grund})`).join(', ')}
            </p>
          )}

          {/* WAS EIN EINBLASSTOP FREISTELLT, UND WAS NICHT.
              Er nimmt die Nachweisordner aus der Pflicht, das
              Einblasprotokoll ausdrücklich NICHT: gerade dann will man
              sehen, wie weit es ging. */}
          <div className="hinweis">
            Das Einblasprotokoll bleibt Pflicht. Gerade beim Stop ist es der
            Nachweis, wie weit es ging.
            {d.anhaenge?.length === 1
              ? ' Im Ordner liegt ein Protokoll.'
              : d.anhaenge?.length
                ? ` Im Ordner liegen ${d.anhaenge.length} Protokolle.`
                : ' Im Ordner liegt noch keines.'}
          </div>
          {d.hinweis && <div className="hinweis">{d.hinweis}</div>}

          {zuruecknehmen ? (
            <>
              <p className="klein" style={{ marginTop: 0 }}>
                {zuruecknehmen.seite
                  ? `Zurückgenommen wird die Meldung „${zuruecknehmen.seite}". `
                    + 'Eine andere Richtung bleibt stehen.'
                  : 'Zurückgenommen werden ALLE Meldungen dieses Auftrags.'}
              </p>
              <Text name="grund" titel="Warum ist der Stop keiner mehr?"
                    pflicht wert={grund} setze={setGrund} mehrzeilig
                    fehler={fehler}
                    hinweis="Der Satz steht später im Verlauf und beantwortet
                             die Frage, warum die Abgabe doch komplett wurde." />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft || !grund.trim()}
                        onClick={zurueck}>
                  {laeuft ? 'Einen Moment' : 'Stop zurücknehmen'}
                </button>
                <button className="knopf" disabled={laeuft}
                        onClick={() => setZuruecknehmen(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : (d.offen && !weitere) ? (
            <>
              {/* AN DIE TELEKOM: die Angaben des Monteurs stehen schon im Text. */}
              {darfSenden && tk && (
                <div style={{ marginTop: 8 }}>
                  <h3 style={{ margin: '6px 0' }}>An die Telekom melden</h3>
                  {tk.gesendet_am && <div className="hinweis erledigt">Gemeldet am {zeitpunkt(tk.gesendet_am)}. Noch einmal senden geht.</div>}
                  {tk.fehlt && <div className="hinweis warnung">{tk.fehlt}</div>}
                  <Text name="an" titel="An" wert={tkAn} setze={setTkAn} breite={420} fehler={fehler}
                        hinweis="Aus der Konfiguration (Einblasstop), hier für diese Meldung änderbar." />
                  <Text name="betreff" titel="Betreff" wert={tkBetreff} setze={setTkBetreff} fehler={fehler} />
                  <Text name="text" titel="Text" wert={tkText} setze={setTkText} mehrzeilig fehler={fehler} />
                  <p className="klein leise" style={{ margin: '4px 0' }}>
                    Anhang: {(tk.anhaenge ?? []).length
                      ? (tk.anhaenge ?? []).map((x: any) => x.dateiname).join(', ')
                      : 'keiner'}
                  </p>
                  {tkErgebnis && (
                    <div className={'hinweis' + (tkErgebnis.gesendet ? ' erledigt' : ' fehler')}>
                      {tkErgebnis.gesendet ? `Gesendet an ${tkErgebnis.an}.` : `Nicht gesendet: ${tkErgebnis.grund ?? 'unbekannt'}`}
                    </div>
                  )}
                </div>
              )}
              <div className="reihe" style={{ marginTop: 12 }}>
                {darfSenden && tk && (
                  <button className="knopf haupt" disabled={laeuft || !!tk.fehlt || !tkAn.trim()} onClick={anTelekom}>
                    {laeuft ? 'Einen Moment' : tk.gesendet_am ? 'Noch einmal an die Telekom senden' : 'An die Telekom senden'}
                  </button>
                )}
                {darfMelden && (
                  <button className="knopf" disabled={laeuft} onClick={() => setWeitere(true)}>
                    Weitere Richtung melden
                  </button>
                )}
                {darfMelden && (
                  <button className="knopf" disabled={laeuft} onClick={() => setZuruecknehmen({})}>
                    Alle zurücknehmen
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="feldreihe">
                <Text name="meter" titel="Wie weit ging es?" pflicht art="number"
                      wert={meter} setze={setMeter} breite={180}
                      fehler={fehler} hinweis="Meter ab dem Ansatzpunkt." />
                <Auswahl name="seite" titel="Aus welcher Richtung?" pflicht
                         wert={seite} setze={setSeite} breite={320}
                         fehler={fehler}
                         optionen={(d.seiten ?? []).map((s: string) =>
                           ({ wert: s, text: s }))}
                         /* EIN PFLICHTFELD OHNE AUSWAHL IST EINE SACKGASSE.
                            Steht `EINBLASSTOP_SEITEN` leer, stand hier eine
                            leere Liste und darunter ein grauer Knopf - und
                            niemand konnte sehen, WARUM. */
                         hinweis={(d.seiten ?? []).length
                           ? undefined
                           : 'Es steht keine Richtung zur Auswahl. Sie werden '
                             + 'in der Konfiguration unter „Einblasstop" '
                             + 'gepflegt (EINBLASSTOP_SEITEN).'} />
              </div>
              <Text name="bemerkung" titel="Was wurde beobachtet?" mehrzeilig
                    wert={bemerkung} setze={setBemerkung}
                    fehler={fehler}
                    hinweis="Freiwillig. Kein Auswahlfeld: die Ursache kennt
                             vor Ort niemand sicher." />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft} onClick={melden}>
                  {laeuft ? 'Einen Moment'
                          : d.offen ? 'Weitere Richtung melden'
                                    : 'Einblasstop melden'}
                </button>
                {d.offen && (
                  <button className="knopf" disabled={laeuft}
                          onClick={() => setWeitere(false)}>
                    Abbrechen
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}

/** Alle offenen Einblasstops. Steht in der Abrechnung, wo das Teilaufmaß
 *  entsteht: ein Stop heißt Teilaufmaß jetzt, Schlussaufmaß später. */
export function EinblasstopListe({ oeffne }: { oeffne: (id: string, reiter?: string) => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  useEffect(() => { api.einblasstopListe().then(setZeilen).catch(() => setZeilen([])); }, []);
  if (!zeilen) return null;

  return (
    <Karte titel="Offene Einblasstops"
           rechts={<span className="weg">{zeilen.length}</span>}>
      {!zeilen.length ? (
        <Leer text="Kein gemeldeter Einblasstop. Ein leerer Kasten ist hier die
                    gute Nachricht." />
      ) : (
        <table className="liste">
          <thead>
            <tr>
              <th style={{ width: 110 }}>KLS</th>
              <th>Baustelle</th>
              <th style={{ width: 90 }} className="zahl">Meter</th>
              <th style={{ width: 220 }}>Richtung</th>
              <th style={{ width: 130 }}>gemeldet</th>
              <th>Bemerkung</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.id} onClick={() => oeffne(z.id)}>
                <td>{z.kls_id}</td>
                <td>{z.strasse} {z.hausnummer}, {z.ort_text}</td>
                <td className="zahl">{menge(z.einblasstop_meter, 'm')}</td>
                <td className="klein">{z.einblasstop_seite}</td>
                <td className="klein leise">
                  {datum(z.einblasstop_gemeldet_am)}
                </td>
                <td className="klein leise">{z.einblasstop_bemerkung}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Karte>
  );
}


// ============================================================================
// 2. Verfügbarkeit
// ============================================================================

/* DIESE TAFEL TRAEGT KEINE `Date`-OBJEKTE MEHR (10.09.2026).
   ==========================================================================
   ORIGINALTEXT MARVIN (mit 2 Bildschirmfotos)
     „bild 1: von kamil, deutsche zeitzone, bild 2: mein pc, zypern zeitzone.
      aber das problem sind die wochentage, datum."

   `montagVon` setzte `setHours(12, 0, 0, 0)` und rechnete mit den lokalen
   Gettern - also auf der Uhr des BETRACHTERS -, waehrend `iso()` den Tag in
   BERLIN las. Der Mittag hat den Bruch gedaempft und nicht behoben: er
   verschiebt die Stunde, in der die beiden Uhren auseinandergehen, von
   Mitternacht auf den Mittag, und in einer Zone jenseits von 12 Stunden
   Versatz kippt er ganz.

   > **Ein Wert, den 2 Uhren lesen, ist 2 Werte.** Ein Tag ist jetzt
   > „2026-09-07" und sonst nichts; die Regeln stehen in `teile/rastertage.ts`
   > und werden in Pruefstand 022 gemessen. */

const WOCHENTAG = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * Wer wann nicht kann.
 *
 * EIN TAG OHNE EINTRAG GILT ALS FREI. Das ist die Voreinstellung des Bestands
 * und bleibt so: wer sie umdreht, sperrt über Nacht jeden Termin. Deshalb
 * zeigt die Tafel auch nicht „frei" in Grün, sondern nur, was jemand
 * ausdrücklich gemeldet hat.
 */
export function VerfuegbarkeitTafel({ ich }: { ich: Anmelder }) {
  const [ab, setAb] = useState<Tag>(() => montagVon(heuteTag()));
  const [d, setD] = useState<any>(null);
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const [firma, setFirma] = useState<string>('');
  const [hinweis, setHinweis] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfPflegen = darf(ich, 'verfuegbarkeit.pflegen');

  const WOCHEN = 4;
  const tage = useMemo(() => tageAb(ab, WOCHEN * 7), [ab]);

  async function laden() {
    const von = tage[0]!;
    const bis = tage[tage.length - 1]!;
    setD(await api.verfuegbarkeit(von, bis));
  }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, [ab]);
  useFrisch(laden);

  useEffect(() => {
    if (!firma && d?.firmen?.length) setFirma(d.firmen[0].id);
  }, [d]);

  if (!d) return null;

  // Schlüssel: Firma + Tag. Zwei Einträge auf denselben Tag kann es nicht
  // geben, dafür sorgt der eindeutige Index in der Datenbank.
  const belegt = new Map<string, any>();
  for (const z of d.zeilen) {
    belegt.set(z.subunternehmer_id + '|' + String(z.datum).slice(0, 10), z);
  }

  const um = (tag: string) => {
    if (!darfPflegen) return;
    setWahl((w) => {
      const n = new Set(w);
      if (n.has(tag)) n.delete(tag); else n.add(tag);
      return n;
    });
  };

  async function setzen(zustand: 'verfuegbar' | 'urlaub' | 'frei') {
    if (!wahl.size || !firma) return;
    setLaeuft(true); setFehler(null);
    try {
      await api.verfuegbarkeitSetzen({
        subunternehmer_id: firma, tage: [...wahl], zustand,
        hinweis: hinweis || null });
      setWahl(new Set()); setHinweis('');
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const heute = heuteTag();

  return (
    <>
      <Fehlerkasten lage={fehler} />

      <Karte
        titel="Wer wann nicht kann"
        rechts={
          <span className="reihe">
            <button className="knopf klein" onClick={() => {
              setAb(plusTage(ab, -WOCHEN * 7)); setWahl(new Set());
            }}>zurück</button>
            <button className="knopf klein" onClick={() => {
              setAb(montagVon(heuteTag())); setWahl(new Set());
            }}>heute</button>
            <button className="knopf klein" onClick={() => {
              setAb(plusTage(ab, WOCHEN * 7)); setWahl(new Set());
            }}>vor</button>
          </span>
        }
      >
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ein Tag ohne Eintrag gilt als frei. Eingetragen wird nur, was jemand
          ausdrücklich gemeldet hat: <b>verfügbar</b> heißt zugesagt,{' '}
          <b>Urlaub</b> heißt gesperrt.
        </p>

        <div className="verftafel">
          <div className="verfkopf">
            <div />
            {WOCHENTAG.map((w) => <div key={w} className="verfwt">{w}</div>)}
          </div>
          {d.firmen.map((f: any) => (
            <div key={f.id} className="verfzeile">
              <div className="verffirma">
                <b>{f.firma}</b>
                <div className="klein leise">
                  {f.bis_wann
                    ? 'gepflegt bis ' + datum(f.bis_wann)
                    : 'nichts gepflegt'}
                </div>
              </div>
              {tage.map((t) => {
                const s = t;
                const z = belegt.get(f.id + '|' + s);
                const gewaehlt = firma === f.id && wahl.has(s);
                const kls = ['verftag'];
                if (z?.zustand === 'urlaub') kls.push('urlaub');
                else if (z?.zustand === 'verfuegbar') kls.push('verfuegbar');
                if (istWochenende(t)) kls.push('wochenende');
                if (s === heute) kls.push('heute');
                if (gewaehlt) kls.push('gewaehlt');
                return (
                  <button
                    key={s} className={kls.join(' ')}
                    disabled={!darfPflegen}
                    title={z ? `${z.zustand}${z.hinweis ? ': ' + z.hinweis : ''}`
                             : 'kein Eintrag, gilt als frei'}
                    onClick={() => { setFirma(f.id); um(s); }}
                  >
                    <span className="verfzahl">{Number(t.slice(8, 10))}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {!d.firmen.length && (
            <Leer text="Es ist keine aktive Firma hinterlegt." />
          )}
        </div>

        {darfPflegen && (
          <div className="reihe" style={{ marginTop: 14, alignItems: 'flex-end' }}>
            <Text name="verfhinweis" titel="Hinweis (freiwillig)" breite={280}
                  wert={hinweis} setze={setHinweis}
                  platzhalter="Betriebsurlaub, Krankheit, andere Baustelle" />
            <button className="knopf haupt" disabled={!wahl.size || laeuft}
                    onClick={() => setzen('verfuegbar')}>
              {wahl.size ? `${wahl.size} als verfügbar` : 'Als verfügbar'}
            </button>
            <button className="knopf" disabled={!wahl.size || laeuft}
                    onClick={() => setzen('urlaub')}>
              als Urlaub sperren
            </button>
            <button className="knopf" disabled={!wahl.size || laeuft}
                    onClick={() => setzen('frei')}>
              Eintrag entfernen
            </button>
            {!wahl.size && (
              <span className="klein leise">
                Tage anklicken, dann eintragen. Mehrere gehen zusammen.
              </span>
            )}
          </div>
        )}
      </Karte>

      {/* Die Pflegeübersicht. Früher war das ein EIGENER Menüpunkt
      , und er beantwortete genau eine Frage:
          von wem fehlt die Meldung. Hier ist es eine Spalte. */}
      <Karte titel="Wer hat wie weit gepflegt">
        <table className="liste">
          <thead>
            <tr>
              <th>Firma</th>
              <th style={{ width: 130 }}>Gewerke</th>
              <th style={{ width: 160 }}>gepflegt bis</th>
              <th style={{ width: 120 }} className="zahl">Einträge</th>
              <th style={{ width: 200 }}></th>
            </tr>
          </thead>
          <tbody>
            {d.firmen.map((f: any) => {
              const tageVoraus = f.bis_wann
                ? Math.round((new Date(f.bis_wann).getTime() - Date.now())
                             / 86400000)
                : -1;
              return (
                <tr key={f.id} style={{ cursor: 'default' }}>
                  <td><b>{f.firma}</b></td>
                  <td className="klein leise">
                    {String(f.gewerke ?? '').replace(/[{}"]/g, '') || '-'}
                  </td>
                  <td className="klein">
                    {f.bis_wann ? datum(f.bis_wann)
                                : <span className="rot">nichts gepflegt</span>}
                  </td>
                  <td className="zahl">{f.eintraege}</td>
                  <td className="klein leise">
                    {tageVoraus < 0
                      ? 'Jeder Tag gilt als frei.'
                      : tageVoraus < 7
                        ? `Nur noch ${tageVoraus} Tage im Voraus.`
                        : `${tageVoraus} Tage im Voraus.`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Karte>
    </>
  );
}


// ============================================================================
// 3. Pollercodes
// ============================================================================

/**
 * Die Sperrpfosten, ihre Straßen und die Codes.
 *
 * KEIN CODE IM PAPIER. Er wird dem Subunternehmer zum Termin genannt und
 * steht in keinem Dokument, das das Haus verlässt. Deshalb steht er auch
 * nicht im Protokoll: ein Protokoll, das man lesen darf, ist kein Ort für
 * einen Schlüssel.
 */
export function PollerKarte({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [neu, setNeu] = useState<any | null>(null);
  const [gebietNeu, setGebietNeu] = useState<string | null>(null);
  const [strasseNeu, setStrasseNeu] = useState<any | null>(null);
  const [nachtrag, setNachtrag] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfPflegen = darf(ich, 'poller.pflegen');

  async function laden() { setD(await api.poller()); }
  useEffect(() => { laden().catch(() => setD({ gebiete: [], strassen: [], codes: [] })); }, []);
  useFrisch(laden);
  if (!d) return null;

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const b = await api.pollerCode(neu);
      setNeu(null); setNachtrag(b); await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function gebietSpeichern() {
    setLaeuft(true); setFehler(null);
    try {
      await api.pollerGebiet(gebietNeu ?? '');
      setGebietNeu(null); await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function strasseSpeichern() {
    setLaeuft(true); setFehler(null);
    try {
      await api.pollerStrasse(strasseNeu);
      setStrasseNeu(null); await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const heute = heuteIso();

  return (
    <>
      {nachtrag && (
        <div className="hinweis">
          <b>Der Code ist hinterlegt</b>, gültig bis{' '}
          {datum(nachtrag.gueltig_bis)}.
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            {nachtrag.nachgetragen
              ? `In ${nachtrag.nachgetragen} `
                + (nachtrag.nachgetragen === 1 ? 'Auftrag' : 'Aufträge')
                + ` nachgetragen: ${nachtrag.auftraege.join(', ')}.`
              : 'Kein Auftrag hat in dieser Zeit einen Termin in dem Gebiet. '
                + 'Er wird beim nächsten Termin gefunden.'}
          </div>
        </div>
      )}

      <Karte
        titel="Pollergebiete"
        rechts={darfPflegen ? (
          <span className="reihe">
            <button className="knopf klein" onClick={() => setGebietNeu('')}>
              Gebiet anlegen
            </button>
            {!!d.gebiete.length && (
              <button className="knopf klein" onClick={() => {
                setNachtrag(null);
                setNeu({ gebiet_id: d.gebiete[0].id, code: '',
                         gueltig_von: heuteIso(),
                         bemerkung: '' });
              }}>
                Code hinterlegen
              </button>
            )}
          </span>
        ) : undefined}
      >
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ohne gültigen Code kommt der Tiefbauer nicht in die Zone, und das
          merkt er vor Ort. Der Code steht in keinem Papier: er wird zum Termin
          genannt.
        </p>
        {!d.gebiete.length ? (
          <Leer text="Kein Pollergebiet hinterlegt." />
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th>Gebiet</th>
                <th style={{ width: 110 }} className="zahl">Straßen</th>
                <th style={{ width: 130 }}>gültiger Code</th>
                <th style={{ width: 150 }}>gültig bis</th>
                <th>Bemerkung</th>
              </tr>
            </thead>
            <tbody>
              {d.gebiete.map((g: any) => {
                const codes = d.codes.filter((c: any) => c.gebiet_id === g.id);
                const gilt = codes.find((c: any) =>
                  !c.gueltig_bis || String(c.gueltig_bis).slice(0, 10) >= heute);
                return (
                  <tr key={g.id} style={{ cursor: 'default' }}>
                    <td><b>{g.name}</b></td>
                    <td className="zahl">{g.strassen}</td>
                    <td>
                      {gilt
                        ? <code>{gilt.code}</code>
                        : <span className="rot">kein gültiger Code</span>}
                    </td>
                    <td className="klein leise">
                      {gilt?.gueltig_bis ? datum(gilt.gueltig_bis)
                                         : gilt ? 'unbefristet' : ''}
                    </td>
                    <td className="klein leise">{gilt?.bemerkung}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Karte>

      <Karte
        titel="Straßen in einem Pollergebiet"
        rechts={darfPflegen && d.gebiete.length ? (
          <button className="knopf klein" onClick={() => setStrasseNeu({
            gebiet_id: d.gebiete[0].id, strasse: '', ort: '', notiz: '' })}>
            Straße zuordnen
          </button>
        ) : undefined}
      >
        {!d.strassen.length ? (
          <Leer text="Keine Straße zugeordnet. Ohne Zuordnung meldet sich der
                      Poller bei keinem Auftrag." />
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th>Straße</th><th style={{ width: 200 }}>Ort</th>
                <th style={{ width: 220 }}>Gebiet</th><th>Notiz</th>
              </tr>
            </thead>
            <tbody>
              {d.strassen.map((s: any) => (
                <tr key={s.id} style={{ cursor: 'default' }}>
                  <td>{s.strasse}</td>
                  <td>{s.ort}</td>
                  <td className="klein leise">{s.gebiet}</td>
                  <td className="klein leise">{s.notiz}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>

      {neu && (
        <Dialog titel="Pollercode hinterlegen" breite={560} laeuft={laeuft}
                zu={() => setNeu(null)}>
          <Fehlerkasten lage={fehler} />
          <Auswahl name="gebiet_id" titel="Gebiet" pflicht breite="100%"
                   wert={neu.gebiet_id} fehler={fehler}
                   setze={(w) => setNeu({ ...neu, gebiet_id: w })}
                   optionen={d.gebiete.map((g: any) =>
                     ({ wert: g.id, text: g.name }))} />
          <div className="feldreihe">
            <Text name="code" titel="Code" pflicht wert={neu.code}
                  fehler={fehler} breite={200}
                  setze={(w) => setNeu({ ...neu, code: w })} />
            <Text name="gueltig_von" titel="gültig ab" art="date" pflicht
                  wert={neu.gueltig_von} fehler={fehler} breite={200}
                  setze={(w) => setNeu({ ...neu, gueltig_von: w })}
                  hinweis="Das Ende wird gerechnet: ein Monat ab dem Starttag,
                           einschliesslich. So gibt die Stadt ihn heraus." />
          </div>
          <Text name="bemerkung" titel="Bemerkung" breite="100%"
                wert={neu.bemerkung} fehler={fehler}
                setze={(w) => setNeu({ ...neu, bemerkung: w })} />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt" disabled={laeuft || !neu.code.trim()}
                    onClick={speichern}>
              {laeuft ? 'Einen Moment' : 'Hinterlegen'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setNeu(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}

      {gebietNeu !== null && (
        <Dialog titel="Pollergebiet anlegen" breite={520} laeuft={laeuft}
                zu={() => setGebietNeu(null)}>
          <Fehlerkasten lage={fehler} />
          <Text name="name" titel="Name des Gebiets" pflicht breite="100%"
                wert={gebietNeu} setze={setGebietNeu} fehler={fehler}
                platzhalter="Altstadt, Karthause, Fußgängerzone Limburg"
                hinweis="So, wie die Stadt die Zone nennt. Der Name steht
                         später in jeder Meldung." />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt"
                    disabled={laeuft || !gebietNeu.trim()}
                    onClick={gebietSpeichern}>
              {laeuft ? 'Einen Moment' : 'Anlegen'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setGebietNeu(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}

      {strasseNeu && (
        <Dialog titel="Straße einem Pollergebiet zuordnen" breite={600}
                laeuft={laeuft} zu={() => setStrasseNeu(null)}>
          <Fehlerkasten lage={fehler} />
          <Auswahl name="gebiet_id" titel="Gebiet" pflicht breite="100%"
                   wert={strasseNeu.gebiet_id} fehler={fehler}
                   setze={(w) => setStrasseNeu({ ...strasseNeu, gebiet_id: w })}
                   optionen={d.gebiete.map((g: any) =>
                     ({ wert: g.id, text: g.name }))} />
          <div className="feldreihe">
            <Text name="strasse" titel="Straße" pflicht breite={300}
                  wert={strasseNeu.strasse} fehler={fehler}
                  setze={(w) => setStrasseNeu({ ...strasseNeu, strasse: w })} />
            <Text name="ort" titel="Ort" pflicht breite={240}
                  wert={strasseNeu.ort} fehler={fehler}
                  setze={(w) => setStrasseNeu({ ...strasseNeu, ort: w })}
                  hinweis={'Pflicht. „Bahnhofstraße" gibt es in jeder zweiten '
                    + 'Gemeinde.'} />
          </div>
          <Text name="notiz" titel="Notiz" breite="100%"
                wert={strasseNeu.notiz} fehler={fehler}
                setze={(w) => setStrasseNeu({ ...strasseNeu, notiz: w })} />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt"
                    disabled={laeuft || !strasseNeu.strasse.trim()
                              || !strasseNeu.ort.trim()}
                    onClick={strasseSpeichern}>
              {laeuft ? 'Einen Moment' : 'Zuordnen'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setStrasseNeu(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
    </>
  );
}

/**
 * Der Hinweis in der Akte: liegt diese Adresse in einem Pollergebiet?
 *
 * Er zeigt sich NUR, wenn sie es tut. Ein Kasten, in dem meistens „nein"
 * steht, wird nach zwei Wochen nicht mehr gelesen, und dann steht der Bagger
 * vor dem Pfosten.
 */
export function PollerHinweis({ auftragId }: { auftragId: string }) {
  const [d, setD] = useState<any>(null);
  useEffect(() => {
    api.pollerFuerAuftrag(auftragId).then(setD).catch(() => setD(null));
  }, [auftragId]);
  if (!d?.imGebiet) return null;

  return (
    <div className={'hinweis ' + (d.codes.length ? 'warnung' : 'fehler')}>
      <b>Diese Adresse liegt in einem Pollergebiet</b>
      {' '}({d.gebiete.join(', ')}).
      {d.codes.length ? (
        <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
          Gültiger Code: <code>{d.codes[0].code}</code>
          {d.codes[0].gueltig_bis
            && ` (bis ${datum(d.codes[0].gueltig_bis)})`}.
          {' '}Er wird dem Tiefbauer zum Termin genannt und steht in keinem
          Papier.
        </div>
      ) : (
        <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
          {d.hinweis}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// 4. Rechnungspruefung: umgezogen
// ============================================================================
//
// Sie steht seit dem 26.08.2026 in teile/Rechnungspruefung.tsx. Die Tafel
// hier war eine Liste mit drei Knoepfen und einer Spalte "Befunde", in der
// bei jeder Zeile eine graue 0 stand: die Sicht dahinter hatte die Spalte
// seit Migration 032 gar nicht mehr. Was daraus geworden ist, braucht ein
// Pruef- und ein Erfassungsfenster und passt nicht mehr in diese Datei.


// ============================================================================
// 5. Nachrichten von Hand
// ============================================================================

/**
 * Telegram an eine Firma und die Terminbestätigung an den Kunden.
 *
 * DER TESTMODUS BLEIBT DARÜBER. Beide Wege gehen durch dieselbe Funktion wie
 * jede andere Nachricht; solange `sicherheit.telegram_testmodus` auf „ja"
 * steht, geht nichts hinaus und der Versuch steht trotzdem im Protokoll. Der
 * Dialog sagt das hin, statt „gesendet" zu melden und nichts zu tun.
 */
export function NachrichtDialog({
  auftragId, baustelle, zu,
}: {
  auftragId: string; baustelle?: string; zu: () => void;
}) {
  const [reiter, setReiter] = useState<'telegram' | 'mail'>('telegram');
  // Die Firmenliste kommt aus den Stammdaten, nicht aus dem Auftrag: eine
  // Nachricht geht auch an eine Firma, die auf diesem Auftrag (noch) keinen
  // Termin hat.
  const [firmen, setFirmen] = useState<any[]>([]);
  const [firma, setFirma] = useState('');
  const [text, setText] = useState('');
  /* DER ANLASS, DIE GEWERKE UND DIE VORSCHAU.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „beim versenden der telegramnachricht fehlt die vorschau, außerdem die
   *    verschiedenen punkte um was es geht. schaue dir dazu das alte system
   *    an."
   *
   * Angesehen: der Bestand hatte fünf Anlässe, eine Auswahl je Gewerk und
   * eine Vorschau, die sagt AN WEN und WELCHER TEXT. Der Neubau hatte ein
   * Textfeld. Die Vorschau kommt vom SERVER und ist derselbe Aufruf wie der
   * Versand, nur mit `senden: false` - eine Vorschau, die im Browser gebaut
   * wird, ist eine zweite Rechnung über dieselbe Frage. */
  const [anlass, setAnlass] = useState('neu');
  const [gewaehlt, setGewaehlt] = useState<string[] | null>(null);
  const [innendienst, setInnendienst] = useState(false);
  const [vorschau, setVorschau] = useState<any>(null);
  const [freieFirma, setFreieFirma] = useState(false);
  const [mail, setMail] = useState<any>(null);
  const [mailtext, setMailtext] = useState('');
  const [betreff, setBetreff] = useState('');
  // VIER STUFEN, WIE IM BESTAND: Terminankündigung, Erinnerung, letzte
  // Erinnerung, Rückgabe an die Telekom. Sie haben je einen eigenen Text im
  // Register „12_Konfiguration"; der Neubau kannte nur einen.
  const [stufe, setStufe] = useState('erst');
  /* Warum eine Terminmail gerade nicht hinausginge - abgeschaltet, keine
     Adresse, keine Vorlage. Drei verschiedene nächste Schritte. */
  const [terminfehlt, setTerminfehlt] = useState<string | null>(null);
  const [an, setAn] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);

  useEffect(() => {
    api.subunternehmer().then((f) => {
      const aktiv = f.filter((x: any) => x.aktiv);
      setFirmen(aktiv);
      if (aktiv.length) setFirma(aktiv[0].id);
    }).catch(() => {});
    api.kundenmail(auftragId).then((m) => {
      setMail(m);
      const erst = (m.stufen ?? []).find((x: any) => x.wert === 'erst');
      setMailtext(erst?.text ?? '');
      setBetreff(erst?.betreff ?? '');
      setAn(m.an ?? '');
    }).catch(() => {});
  }, [auftragId]);

  async function sendeTelegram() {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      setErgebnis(await api.telegram({
        subunternehmer_id: firma, text, auftrag_id: auftragId }));
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* DIE VORSCHAU WIRD GEHOLT, SOBALD SICH ETWAS ÄNDERT. Sie schreibt nichts:
     derselbe Weg mit `senden: false`. Ein Knopf „Vorschau" wäre ein
     Handgriff, den man beim dritten Mal auslässt - und dann sieht man wieder
     nicht, was hinausgeht. */
  useEffect(() => {
    if (reiter !== 'telegram' || freieFirma) return;
    let weg = false;
    const zeit = setTimeout(() => {
      api.telegramAuftrag(auftragId, {
        anlass, freitext: text, innendienst,
        ...(gewaehlt ? { gewerke: gewaehlt } : {}), senden: false,
      }).then((v) => {
        if (weg) return;
        setVorschau(v);
        /* EIN FEHLER, DEN DER NÄCHSTE VERSUCH BEHEBT, MUSS BEIM NÄCHSTEN
           VERSUCH WEGGEHEN. Hier stand nichts, und deshalb blieb der rote
           Kasten „Bei einer freien Nachricht fehlt der Text" stehen, während
           daneben längst ein Text im Feld war (Marvin, 03.09.2026: „hier
           kommt direkt die rote meldung und geht auch nicht weg"). Ein Kasten,
           der eine behobene Sache anmahnt, wird beim dritten Mal überlesen -
           und dann auch der, der stimmt. */
        setFehler(null);
        // Beim ersten Blick sind alle Gewerke mit Firma gewählt: das ist der
        // Regelfall, und wer weniger will, klickt eines weg.
        if (gewaehlt === null) {
          setGewaehlt(v.gewerke.filter((g: any) => g.firma)
                       .map((g: any) => g.art));
        }
      }).catch((f: any) => { if (!weg) setFehler(ausFehler(f)); });
    }, 250);
    return () => { weg = true; clearTimeout(zeit); };
  }, [auftragId, reiter, anlass, text, innendienst, gewaehlt, freieFirma]);

  async function sendeAnlass() {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      const v = await api.telegramAuftrag(auftragId, {
        anlass, freitext: text, innendienst,
        gewerke: gewaehlt ?? [], senden: true });
      setErgebnis({
        gesendet: v.gesendet > 0,
        grund: v.gesendet ? null
          : v.testmodus ? 'Der Testmodus hält jede Nachricht zurück.'
                        : 'Es gab keinen Empfänger mit Chat.',
        empfaenger: (v.ergebnisse ?? []).map((r: any) => r.firma).join(', '),
        hinweis: (v.uebersprungen ?? []).join(', ') || undefined,
      });
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function sendeMail() {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      /* ZWEI FAMILIEN, ZWEI WEGE. Sie haben andere Vorlagen, andere
         Platzhalter und andere Rückfallregeln; ein Weg mit zwei Bedeutungen
         liesse die Absage eines Tages auf den Text der Terminankündigung
         zurückfallen. */
      if (TERMINARTEN.includes(stufe)) {
        setErgebnis(await api.terminmail(auftragId, {
          art: stufe, gewerk: 'TB', an, betreff, text: mailtext,
          senden: true }));
        return;
      }
      setErgebnis(await api.kundenmailSenden(auftragId, {
        an, stufe, betreff, text: mailtext }));
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Nachricht senden" untertitel={baustelle} breite={680}
            laeuft={laeuft} zu={zu}>
      <div className="reihe" style={{ marginBottom: 12 }}>
        {([['telegram', 'Telegram an eine Firma'],
           /* ER HEISST, WAS ER TUT. „Terminbestätigung an den Kunden" war
              der Name für einen Reiter, der die Mails der TERMIN-
              VEREINBARUNG schickt - und die Bestätigung des Termins gab es
              gar nicht (Marvin, 03.09.2026: „wir können dem kunden hier keine
              terminbestätigung senden, das fehlt noch"). Sie gibt es jetzt,
              und der Reiter trägt beide Familien. */
           ['mail', 'Mail an den Kunden']] as const)
          .map(([r, t]) => (
            <button key={r} className="knopf"
                    onClick={() => { setReiter(r); setErgebnis(null); setFehler(null); }}
                    style={reiter === r
                      ? { borderColor: 'var(--akzent)', color: 'var(--akzent)' }
                      : undefined}>
              {t}
            </button>
          ))}
      </div>

      <Fehlerkasten lage={fehler} />

      {ergebnis && (
        <div className={'hinweis ' + (ergebnis.gesendet ? '' : 'warnung')}>
          {ergebnis.gesendet ? (
            <b>Ist hinaus.</b>
          ) : (
            <>
              <b>Nicht versendet.</b> {ergebnis.grund
                ?? 'Der Testmodus hält jede Nachricht zurück.'}
            </>
          )}
          {ergebnis.empfaenger && (
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              Empfänger: {ergebnis.empfaenger}
            </div>
          )}
          {ergebnis.hinweis && (
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {ergebnis.hinweis}
            </div>
          )}
        </div>
      )}

      {reiter === 'telegram' ? (
        <>
          {/* ZWEI WEGE, UND SIE BEANTWORTEN VERSCHIEDENE FRAGEN. Der obere
              geht an die Firmen DIESES Auftrags, mit Anlass und Vorschau -
              das ist der Regelfall. Der untere geht an EINE beliebige Firma,
              auch an eine ohne Termin hier; er bleibt, weil es ihn braucht,
              und er sagt selbst, wofür. */}
          <Auswahl name="anlass" titel="Worum geht es?" breite="100%"
                   wert={freieFirma ? '' : anlass}
                   setze={(v) => { setFreieFirma(v === ''); if (v) setAnlass(v); }}
                   fehler={fehler}
                   optionen={[
                     ...(vorschau?.anlaesse ?? []).map((x: any) =>
                       ({ wert: x.wert, text: x.text })),
                     { wert: '', text: 'An eine einzelne Firma, freier Text' },
                   ]}
                   hinweis={freieFirma
                     ? 'Geht an eine Firma, die an diesem Auftrag keinen Termin haben muss.'
                     : (vorschau?.anlaesse ?? [])
                         .find((x: any) => x.wert === anlass)?.hinweis} />

          {freieFirma ? (
            <>
              <Auswahl name="subunternehmer_id" titel="An welche Firma?" pflicht
                       breite="100%" wert={firma} setze={setFirma} fehler={fehler}
                       optionen={firmen.map((f: any) =>
                         ({ wert: f.id, text: f.firma }))}
                       hinweis="Der Chat kommt aus den Stammdaten. Eine Chatnummer
                                im Formular könnte niemand gegenprüfen." />
              <Text name="text" titel="Text" pflicht mehrzeilig
                    wert={text} setze={setText} fehler={fehler} />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft || !text.trim()}
                        onClick={sendeTelegram}>
                  {laeuft ? 'Einen Moment' : 'Senden'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* JE GEWERK EIN EMPFÄNGER. An einem Auftrag hängen bis zu drei
                  Firmen; eine Absage geht an die, deren Termin abgesagt ist. */}
              <div className="feld" style={{ width: '100%' }}>
                <label className="klein">An welche Gewerke?</label>
                <div className="reihe" style={{ flexWrap: 'wrap', gap: 10 }}>
                  {(vorschau?.gewerke ?? []).length === 0 && (
                    <span className="klein leise">
                      An diesem Auftrag hängt noch kein Gewerk mit einer Firma.
                    </span>
                  )}
                  {/* EIN HÄKCHEN JE FAHRT, nicht je Gewerk. Der Server bündelt
                      zwei Gewerke derselben Firma zur selben Zeit zu einer
                      Nachricht; zwei Häkchen dafür wären zwei Gelegenheiten,
                      das falsche wegzuklicken. `arten` sind die Gewerke, die
                      darin stecken - die Beschriftung baut der Server
                      (`text`), damit hier keine zweite Fassung entsteht. */}
                  {(vorschau?.gewerke ?? []).map((g: any) => (
                    <label key={(g.arten ?? [g.art]).join('+')} className="klein"
                           style={{ display: 'flex', alignItems: 'center', gap: 5,
                                    opacity: g.firma ? 1 : .45 }}>
                      <input type="checkbox" disabled={!g.firma}
                             checked={(g.arten ?? [g.art])
                               .every((x: string) => (gewaehlt ?? []).includes(x))}
                             onChange={(e) => setGewaehlt((alt) => {
                               const l = alt ?? [];
                               const arten: string[] = g.arten ?? [g.art];
                               return e.target.checked
                                 ? [...l, ...arten.filter((x) => !l.includes(x))]
                                 : l.filter((x) => !arten.includes(x));
                             })} />
                      {g.text ?? (GEWERK_TEXT[g.art] ?? g.art)}
                      {', '}
                      {g.firma ?? 'keine Firma'}
                      {g.firma && !g.chatDa && ' (kein Chat)'}
                    </label>
                  ))}
                  <label className="klein"
                         style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <input type="checkbox" checked={innendienst}
                           onChange={(e) => setInnendienst(e.target.checked)} />
                    auch in den Innendienstchat
                  </label>
                </div>
              </div>

              <Text name="freitext" titel="Zusatztext" mehrzeilig
                    wert={text} setze={setText} fehler={fehler}
                    hinweis={'Kommt eins zu eins unter die Nachricht. Bei '
                             + '„Freie Nachricht" ist er der ganze Text.'} />

              {/* DIE VORSCHAU: AN WEN UND WELCHER TEXT. */}
              <div style={{ marginTop: 12 }}>
                <div className="klein leise" style={{ marginBottom: 6 }}>
                  Vorschau - genau das geht hinaus
                  {vorschau?.testmodus && ' (im Testmodus geht nichts hinaus, '
                    + 'der Versuch steht trotzdem im Protokoll)'}
                </div>
                {/* WAS FEHLT, SAGT DIE VORSCHAU - sie weist nicht ab. Ein
                    leeres Textfeld bei „Freie Nachricht" ist beim Öffnen der
                    Regelfall und kein Fehler. */}
                {vorschau?.fehlt && (
                  <p className="klein" style={{ color: 'var(--neu)' }}>
                    {vorschau.fehlt}
                  </p>
                )}
                {(vorschau?.eintraege ?? []).length === 0
                  && !vorschau?.innendienst && (
                  <p className="klein leise">Kein Empfänger gewählt.</p>
                )}
                {[...(vorschau?.eintraege ?? []),
                  ...(vorschau?.innendienst ? [vorschau.innendienst] : [])]
                  .map((e: any, i: number) => (
                  <div key={i} className="tgvorschau">
                    <div className="reihe klein">
                      <b>{e.firma ?? 'ohne Firma'}</b>
                      {e.gewerk && <span className="leise">
                        {', '}{e.gewerke
                          ? e.gewerke.map((x: string) => GEWERK_TEXT[x] ?? x)
                              .join(' + ')
                          : (GEWERK_TEXT[e.gewerk] ?? e.gewerk)}</span>}
                      <div className="wachsen" />
                      {e.grund
                        ? <span className="merker warnung">geht nicht hinaus</span>
                        : <span className="merker">bereit</span>}
                    </div>
                    {e.grund && <div className="klein rot">{e.grund}</div>}
                    <pre className="tgtext">{e.text}</pre>
                  </div>
                ))}
              </div>

              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt"
                        disabled={laeuft || !!vorschau?.fehlt
                          || ![...(vorschau?.eintraege ?? []),
                               ...(vorschau?.innendienst ? [vorschau.innendienst] : [])]
                              .some((e: any) => !e.grund)}
                        title={vorschau?.fehlt ?? undefined}
                        onClick={sendeAnlass}>
                  {laeuft ? 'Einen Moment' : 'Senden'}
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {!mail?.an && (
            <div className="hinweis warnung">
              Für diesen Kunden ist keine Mailadresse hinterlegt. Sie lässt
              sich hier eintragen, wird aber nicht gespeichert.
            </div>
          )}
          <div className="feldreihe">
            <Text name="an" titel="An" pflicht art="email" breite={320}
                  wert={an} setze={setAn} fehler={fehler} />
            <div className="feld" style={{ width: 280 }}>
              <label>Kunde</label>
              <div style={{ paddingTop: 6 }}>
                {mail?.kunde ?? <span className="leise">unbekannt</span>}
                {mail?.termin && (
                  <div className="klein leise">
                    Tiefbautermin {datum(mail.termin)}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* WELCHE DER VIER MAILS. Früher gab es Erstmail, Reminder,
              letzte Mail und Abbruchmail, jede mit eigenem Betreff und Text.
              Der Neubau kannte eine, und die war ausgedacht. */}
          {/* ZWEI FAMILIEN, EIN AUSWAHLFELD.
              Vor dem Termin geht es um die VEREINBARUNG (Ankündigung, zwei
              Erinnerungen, Rückgabe an die Telekom), danach um den TERMIN
              (Bestätigung, Verschiebung, Absage, Erinnerung). Der Innendienst
              fragt sich nicht, welche Familie es ist - er fragt „was schicke
              ich dem Kunden", und die Antwort ist eine Liste. */}
          <Auswahl name="stufe" titel="Welche Mail" breite="100%" leerText=""
                   wert={stufe} fehler={fehler}
                   setze={(w) => {
                     setStufe(w);
                     if (TERMINARTEN.includes(w)) {
                       /* DIE TERMINMAIL RECHNET DER SERVER. Sie hat andere
                          Platzhalter (beide Termine, der bisherige Tag), und
                          eine zweite Fassung davon im Browser liefe beim
                          ersten Nachtrag auseinander. */
                       api.terminmail(auftragId, { art: w, gewerk: 'TB',
                                                   senden: false })
                         .then((r) => {
                           setBetreff(r.betreff ?? '');
                           setMailtext(r.text ?? '');
                           setTerminfehlt(r.fehlt ?? null);
                         }).catch(() => {});
                       return;
                     }
                     setTerminfehlt(null);
                     const g = (mail?.stufen ?? []).find((x: any) => x.wert === w);
                     setMailtext(g?.text ?? '');
                     setBetreff(g?.betreff ?? '');
                   }}
                   optionen={[
                     ...(mail?.stufen ?? []).map((x: any) =>
                       ({ wert: x.wert, text: x.name })),
                     ...TERMINMAILS.map((x) => ({ wert: x.wert, text: x.text })),
                   ]}
                   hinweis={(mail?.stufen ?? [])
                     .find((x: any) => x.wert === stufe)?.zweck
                     ?? TERMINMAILS.find((x) => x.wert === stufe)?.zweck} />
          {terminfehlt && (
            <div className="hinweis warnung">{terminfehlt}</div>
          )}
          {!TERMINARTEN.includes(stufe)
            && !(mail?.stufen ?? []).find((x: any) => x.wert === stufe)?.text && (
            <div className="hinweis warnung">
              Für diese Stufe steht in der Konfiguration kein Text. Er lässt
              sich hier eintippen - und das ist seit dem 28.08.2026 der
              einzige Weg: es gibt keinen Übernahmelauf mehr, der ihn aus
              anderen Quelle nachliefern könnte.
            </div>
          )}
          <Text name="betreff" titel="Betreff" pflicht breite="100%"
                wert={betreff} setze={setBetreff} fehler={fehler} />
          <Text name="text" titel="Text" pflicht mehrzeilig
                wert={mailtext} setze={setMailtext} fehler={fehler}
                hinweis={'Die Platzhalter in geschweiften Klammern werden '
                  + 'eingesetzt, groß wie klein geschrieben: {Kunde} und '
                  + '{kunde} treffen denselben Wert. Was keinen Wert '
                  + 'hat, bleibt leer und wird gemeldet - ein „{kunde}" in der '
                  + 'versendeten Mail ist peinlicher als eine leere Stelle.'} />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt"
                    disabled={laeuft || !an.trim() || !mailtext.trim()}
                    onClick={sendeMail}>
              {laeuft ? 'Einen Moment' : 'Senden'}
            </button>
          </div>

          {!!mail?.frueher?.length && (
            <div style={{ marginTop: 16 }}>
              <h4 className="klein leise">Schon gesendet</h4>
              <table className="liste">
                <tbody>
                  {mail.frueher.map((f: any, i: number) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                      <td className="klein leise" style={{ width: 150 }}>
                        {zeitpunkt(f.gesendet_am)}
                      </td>
                      <td className="klein">{f.empfaenger}</td>
                      <td className={'klein' + (f.erfolgreich ? '' : ' rot')}>
                        {f.erfolgreich ? f.betreff : f.ergebnis}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
